import { useCallback, useEffect, useRef, useState } from 'react';
import ReferenceScreen from '../../components/reference/ReferenceScreen';
import RowStatus from '../../components/reference/RowStatus';
import FormAlert from '../../components/reference/FormAlert';
import { referenceApi } from '../../services/referenceApi';
import { apiErrorMessage, blankToNull, isNotFound } from '../../services/apiErrors';
import type { NamedResource, NamedRow } from '../../services/referenceApi';

const COPY: Record<NamedResource, { title: string; singular: string; lead: (buyer: string) => string }> = {
  mills: {
    title: 'Mills',
    singular: 'mill',
    lead: (buyer) =>
      `The feed mills ${buyer} buys for. A mill that is switched off stays on past purchases and cannot be picked for new ones.`,
  },
  suppliers: {
    title: 'Suppliers',
    singular: 'supplier',
    lead: (buyer) =>
      `Who ${buyer} buys ingredients from. A supplier that is switched off stays on past purchases and cannot be picked for new ones.`,
  },
};

type Editing = { kind: 'new' } | { kind: 'edit'; row: NamedRow } | null;

function byName(a: NamedRow, b: NamedRow) {
  return a.name.localeCompare(b.name);
}

/** Mills and suppliers: the same list, add, edit and switch-off, told apart by the resource. */
export default function NamedRowsPage({ slug, resource }: { slug: string; resource: NamedResource }) {
  const copy = COPY[resource];
  const [rows, setRows] = useState<NamedRow[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [buyerMissing, setBuyerMissing] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [switchError, setSwitchError] = useState('');
  const nameRef = useRef<HTMLInputElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setLoadError('');
    setBuyerMissing(false);
    setEditing(null);
    setSwitchError('');
    setSwitching(null);
    referenceApi
      .listNamed(slug, resource)
      .then(({ data }) => {
        if (!cancelled) setRows([...data].sort(byName));
      })
      .catch((err) => {
        if (cancelled) return;
        if (isNotFound(err)) setBuyerMissing(true);
        else setLoadError(`We could not load the ${copy.title.toLowerCase()} just now. Check your connection and try again.`);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, resource, copy.title]);

  useEffect(() => {
    if (editing) nameRef.current?.focus();
  }, [editing]);

  const open = useCallback((next: Editing) => {
    setEditing(next);
    setSaveError('');
    setName(next?.kind === 'edit' ? next.row.name : '');
    setCode(next?.kind === 'edit' ? (next.row.code ?? '') : '');
  }, []);

  const place = (saved: NamedRow) =>
    setRows((current) => [...(current ?? []).filter((r) => r.id !== saved.id), saved].sort(byName));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setSaveError('');
    setSaving(true);
    const body = { name: name.trim(), code: blankToNull(code) };
    try {
      const { data } =
        editing.kind === 'new'
          ? await referenceApi.addNamed(slug, resource, body)
          : await referenceApi.editNamed(slug, resource, editing.row.id, body);
      place(data);
      setEditing(null);
      addButtonRef.current?.focus();
    } catch (err) {
      setSaveError(apiErrorMessage(err, `We could not save the ${copy.singular} just now. Check your connection and try again.`));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (row: NamedRow) => {
    setSwitchError('');
    setSwitching(row.id);
    try {
      const { data } = await referenceApi.editNamed(slug, resource, row.id, { active: !row.active });
      place(data);
    } catch (err) {
      setSwitchError(apiErrorMessage(err, `We could not switch ${row.name} ${row.active ? 'off' : 'on'} just now.`));
    } finally {
      setSwitching(null);
    }
  };

  const Singular = copy.singular.charAt(0).toUpperCase() + copy.singular.slice(1);

  return (
    <ReferenceScreen
      slug={slug}
      title={copy.title}
      lead={copy.lead}
      addLabel={`Add ${copy.singular}`}
      onAdd={() => open({ kind: 'new' })}
      addButtonRef={addButtonRef}
      buyerMissing={buyerMissing}
    >
      {editing && (
        <form onSubmit={handleSubmit} className="mt-8 space-y-5 rounded-md border border-line bg-surface p-6" noValidate>
          <h2 className="text-heading text-ink">{editing.kind === 'new' ? `New ${copy.singular}` : `Edit ${editing.row.name}`}</h2>
          <FormAlert lead={`${Singular} not saved.`} message={saveError} />
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="row-name" className="label">
                Name
              </label>
              <input id="row-name" ref={nameRef} type="text" required maxLength={200} value={name} onChange={(e) => setName(e.target.value)} className="input" />
            </div>
            <div>
              <label htmlFor="row-code" className="label">
                Code
              </label>
              <input
                id="row-code"
                type="text"
                maxLength={50}
                aria-describedby="row-code-hint"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="input"
              />
              <p id="row-code-hint" className="mt-1 text-caption text-ink-muted">
                The buyer&apos;s own code for this {copy.singular}, if it has one.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary" disabled={saving || !name.trim()}>
              {saving ? 'Saving' : editing.kind === 'new' ? `Save ${copy.singular}` : 'Save changes'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="mt-8">
        {switchError && <FormAlert lead="Not switched." message={switchError} />}
        {loadError && <FormAlert lead={`${copy.title} not loaded.`} message={loadError} />}
        {rows === null && !loadError && (
          <p className="text-body text-ink-muted" role="status">
            Loading {copy.title.toLowerCase()}
          </p>
        )}
        {rows !== null && rows.length === 0 && <p className="text-body text-ink-muted">No {copy.title.toLowerCase()} yet.</p>}
        {rows !== null && rows.length > 0 && (
          // The table keeps its columns and scrolls inside its own box on a narrow screen. The box is
          // relative so the hidden Actions label stays inside it and cannot widen the page.
          <div className="relative overflow-x-auto">
          <table className="w-full border-collapse text-body">
            <caption className="sr-only">{copy.title}</caption>
            <thead>
              <tr className="border-b border-line-strong text-left text-caption font-semibold text-ink-muted">
                <th scope="col" className="py-2 pr-4">
                  Name
                </th>
                <th scope="col" className="py-2 pr-4">
                  Code
                </th>
                <th scope="col" className="py-2 pr-4">
                  Status
                </th>
                <th scope="col" className="py-2 text-right">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className={`border-b border-line ${row.active ? 'text-ink' : 'text-ink-muted'}`}>
                  <td className="py-3 pr-4 font-semibold">{row.name}</td>
                  <td className="py-3 pr-4 tabular-nums">{row.code ?? ''}</td>
                  <td className="py-3 pr-4">
                    <RowStatus active={row.active} />
                  </td>
                  <td className="py-3 text-right">
                    <div className="flex justify-end gap-3">
                      <button type="button" className="text-caption font-semibold text-navy underline-offset-4 hover:underline" onClick={() => open({ kind: 'edit', row })}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-caption font-semibold text-navy underline-offset-4 hover:underline disabled:opacity-60"
                        disabled={switching === row.id}
                        onClick={() => toggle(row)}
                      >
                        {row.active ? 'Switch off' : 'Switch on'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </ReferenceScreen>
  );
}
